
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface RelatedEntityData {
	id: string;
	name: string;
	entityType: 'candidate' | 'offer' | 'company' | 'match' | 'contact';
}

export const useRelatedEntities = (relations: {
	candidates: string[];
	offers: string[];
	companies: string[];
	matches: string[];
	contacts: string[];
}) => {
	// Fetch candidates data
	const candidatesQuery = useQuery({
		queryKey: ["candidates", relations.candidates],
		queryFn: async () => {
			if (!relations.candidates || relations.candidates.length === 0) return [];
			
			const { data, error } = await supabase
				.from('candidates')
				.select('id, name')
				.in('id', relations.candidates);
				
			if (error) {
				console.error("Error fetching candidates:", error);
				return [];
			}
			
			return (data || []).map(item => ({
				id: item.id,
				name: item.name,
				entityType: 'candidate' as const
			}));
		},
		enabled: relations.candidates && relations.candidates.length > 0
	});
	
	// Fetch job offers data
	const offersQuery = useQuery({
		queryKey: ["jobs", relations.offers],
		queryFn: async () => {
			if (!relations.offers || relations.offers.length === 0) return [];
			
			const { data, error } = await supabase
				.from('jobs')
				.select('id, title')
				.in('id', relations.offers);
				
			if (error) {
				console.error("Error fetching job offers:", error);
				return [];
			}
			
			return (data || []).map(item => ({
				id: item.id,
				name: item.title,
				entityType: 'offer' as const
			}));
		},
		enabled: relations.offers && relations.offers.length > 0
	});
	
	// Fetch companies data
	const companiesQuery = useQuery({
		queryKey: ["companies", relations.companies],
		queryFn: async () => {
			if (!relations.companies || relations.companies.length === 0) return [];
			
			const { data, error } = await supabase
				.from('companies')
				.select('id, name')
				.in('id', relations.companies);
				
			if (error) {
				console.error("Error fetching companies:", error);
				return [];
			}
			
			return (data || []).map(item => ({
				id: item.id,
				name: item.name,
				entityType: 'company' as const
			}));
		},
		enabled: relations.companies && relations.companies.length > 0
	});
	
	// Fetch matches data
	const matchesQuery = useQuery({
		queryKey: ["matches", relations.matches],
		queryFn: async () => {
			if (!relations.matches || relations.matches.length === 0) return [];
			
			const { data, error } = await supabase
				.from('matches')
				.select('id, job:job_id(title), candidate:candidate_id(name)')
				.in('id', relations.matches);
				
			if (error) {
				console.error("Error fetching matches:", error);
				return [];
			}
			
			return (data || []).map(item => ({
				id: item.id,
				name: item.candidate?.name 
					? `${item.candidate.name}${item.job?.title ? ` - ${item.job.title}` : ''}`
					: (item.job?.title || 'Match'),
				entityType: 'match' as const
			}));
		},
		enabled: relations.matches && relations.matches.length > 0
	});
	
	// Fetch contacts data
	const contactsQuery = useQuery({
		queryKey: ["contacts", relations.contacts],
		queryFn: async () => {
			if (!relations.contacts || relations.contacts.length === 0) return [];
			
			const { data, error } = await supabase
				.from('contacts')
				.select('id, name')
				.in('id', relations.contacts);
				
			if (error) {
				console.error("Error fetching contacts:", error);
				return [];
			}
			
			return (data || []).map(item => ({
				id: item.id,
				name: item.name,
				entityType: 'contact' as const
			}));
		},
		enabled: relations.contacts && relations.contacts.length > 0
	});
	
	const isLoading = 
		(relations.candidates?.length > 0 && candidatesQuery.isLoading) ||
		(relations.offers?.length > 0 && offersQuery.isLoading) ||
		(relations.companies?.length > 0 && companiesQuery.isLoading) ||
		(relations.matches?.length > 0 && matchesQuery.isLoading) ||
		(relations.contacts?.length > 0 && contactsQuery.isLoading);
	
	const entities: {
		candidates: RelatedEntityData[];
		offers: RelatedEntityData[];
		companies: RelatedEntityData[];
		matches: RelatedEntityData[];
		contacts: RelatedEntityData[];
	} = {
		candidates: candidatesQuery.data || [],
		offers: offersQuery.data || [],
		companies: companiesQuery.data || [],
		matches: matchesQuery.data || [],
		contacts: contactsQuery.data || []
	};
	
	return {
		entities,
		isLoading,
		error: 
			candidatesQuery.error || 
			offersQuery.error || 
			companiesQuery.error || 
			matchesQuery.error || 
			contactsQuery.error
	};
};
